import {
  CHROME_EXTENSION_ID,
  CHROME_EXTENSION_MIN_VERSION,
} from "../constants";
import { isExtensionRuntime } from "../utils/appSurface";
import {
  ExtensionInstalledResponse,
  getChromeExtensionSettingsUrl,
  resolveWebExtensionStatus,
  WebExtensionStatus,
} from "../utils/extensionStatus";

type ExternalExtensionMessage = {
  is_installed?: boolean;
  open_sidepanel?: boolean;
  open_extension_settings?: boolean;
  set_auth?: string;
  set_workspace?: string | null;
};

const sendExternalMessage = <T>(
  message: ExternalExtensionMessage,
  timeoutMs = 800,
): Promise<T | null> => {
  if (
    isExtensionRuntime() ||
    typeof chrome === "undefined" ||
    !chrome.runtime?.sendMessage ||
    !CHROME_EXTENSION_ID
  ) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: T | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(value);
    };
    const timeoutId = window.setTimeout(() => finish(null), timeoutMs);

    try {
      chrome.runtime.sendMessage(
        CHROME_EXTENSION_ID,
        message,
        (response: T) => {
          // Reading lastError prevents Chrome from logging an expected error when
          // the extension is absent or cannot receive external messages.
          if (chrome.runtime.lastError) {
            finish(null);
            return;
          }
          finish(response ?? null);
        },
      );
    } catch {
      finish(null);
    }
  });
};

export const getWebExtensionStatus = async (): Promise<WebExtensionStatus> => {
  const response = await sendExternalMessage<ExtensionInstalledResponse>({
    is_installed: true,
  });
  return resolveWebExtensionStatus(response, CHROME_EXTENSION_MIN_VERSION);
};

export const syncWebExtensionAuth = async (
  token: string,
  workspaceId?: string | null,
): Promise<boolean> => {
  if (!token.trim()) return false;
  const response = await sendExternalMessage<boolean>(
    {
      set_auth: token,
      set_workspace: workspaceId || null,
    },
    1400,
  );
  return Boolean(response);
};

export const openWebExtension = async (): Promise<boolean> => {
  const response = await sendExternalMessage<{ success?: boolean }>(
    {
      open_sidepanel: true,
    },
    1400,
  );
  return Boolean(response?.success);
};

export const openWebExtensionSettings = async (): Promise<boolean> => {
  const response = await sendExternalMessage<{ success?: boolean }>(
    {
      open_extension_settings: true,
    },
    1400,
  );
  return Boolean(response?.success);
};

export const getWebExtensionSettingsUrl = (): string =>
  getChromeExtensionSettingsUrl(CHROME_EXTENSION_ID);
